/**
 * GTM `signUp` conversion event.
 *
 * Ported from the Laravel app, which flashed the payload to the session on
 * activation and rendered a `dataLayer.push` in the blade of whatever page the
 * redirect landed on:
 *
 *   session()->flash('gtm_signup', [...])   // after verification
 *   @if(session('gtm_signup')) <script> dataLayer.push(...) </script> @endif
 *
 * There is no server session to flash through here, and the verify page itself
 * deliberately has no GTM container on it (it lives in the `(auth)` route
 * group, which is excluded - see components/GoogleTagManager.tsx). So the
 * mechanism is the same in shape: stash the payload on successful
 * verification, then push it from the redirect destination, which does have
 * the container. `sessionStorage` is the flash store - the codebase already
 * uses it for exactly this kind of one-shot handoff (`justRegistered`,
 * `showWhosLearning`).
 *
 * The event fires on VERIFICATION, never on registration - an unverified
 * account is not a signup as far as the funnel is concerned.
 */

const STORAGE_KEY = "gtm_signup";

/**
 * Mirrors the blade's `student: [{...}]` entry exactly. The container's tags
 * read these field names, so they are a contract with GTM - renaming one here
 * silently breaks the tag on the other side.
 */
export type GtmSignupStudent = {
  name: string;
  email: string;
  mobile: string;
  gender: string;
  region: string;
};

type DataLayerWindow = Window & { dataLayer?: unknown[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Blade interpolated whatever the model returned and rendered an empty string
 * for null, so a missing field must stay an empty string rather than becoming
 * "undefined" or dropping out of the payload - GTM tags that map these to
 * required fields would otherwise see the literal text "undefined".
 */
function str(value: unknown): string {
  if (value == null) {
    return "";
  }
  return typeof value === "string" ? value : String(value);
}

/**
 * `region` comes back as a relation, not a scalar - elsewhere in the app it is
 * read as `{ slug?: string }` (see the student/teacher layouts). The blade's
 * `$student->region` stringified the model, so prefer a human-readable name and
 * fall back to the slug.
 */
function regionOf(user: Record<string, unknown>): string {
  const region = user.region;

  if (isRecord(region)) {
    return str(region.name ?? region.title ?? region.slug);
  }

  return str(region);
}

/**
 * Builds the payload from an `/api/me` user. Field names differ from the
 * Laravel model (`fullname`/`phone` there), and sub-accounts may carry only
 * first/last name, so each field is resolved through the aliases this API
 * actually uses.
 */
export function buildGtmSignupStudent(user: unknown): GtmSignupStudent {
  if (!isRecord(user)) {
    return { name: "", email: "", mobile: "", gender: "", region: "" };
  }

  const composed = [str(user.fname), str(user.lname)].filter(Boolean).join(" ");

  return {
    name: str(user.fullname ?? user.name) || composed,
    email: str(user.email),
    mobile: str(user.phone ?? user.mobile),
    gender: str(user.gender),
    region: regionOf(user),
  };
}

/** The `session()->flash()` half: hold the payload for the redirect destination. */
export function stashGtmSignup(student: GtmSignupStudent): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(student));
  } catch {
    // Private-mode / quota / storage-blocked. Losing an analytics event is
    // never a reason to fail the verification it is attached to.
  }
}

/**
 * The `@if(session('gtm_signup'))` half: read once and clear. Removing before
 * returning is what makes this single-use - it keeps React's double-invoked
 * effects in development, and a remount on the destination page, from pushing
 * the same conversion twice.
 */
export function consumeGtmSignup(): GtmSignupStudent | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    window.sessionStorage.removeItem(STORAGE_KEY);

    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? buildGtmSignupStudent(parsed) : null;
  } catch {
    // Unparseable leftover from an older shape - drop it rather than throw.
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing left to do */
    }
    return null;
  }
}

/**
 * Pushes onto `dataLayer` directly rather than waiting for gtm.js. dataLayer is
 * a queue by design: the container replays anything already in it once it
 * loads, so an event pushed before the script arrives is not lost.
 */
export function pushGtmSignup(student: GtmSignupStudent): void {
  if (typeof window === "undefined") {
    return;
  }

  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "signUp",
    student: [student],
  });
}
